import { useLocation, useNavigate } from 'react-router-dom';
import { motion } from 'framer-motion';
import { Home, Search, CalendarCheck, User as UserIcon, Bot } from 'lucide-react';
import { useLanguage } from '../context/LanguageContext';
import { useAuth } from '../context/AuthContext';

/**
 * MobileBottomTabBar — Instagram-style persistent tab bar on mobile only.
 *
 * 5 tabs: Home · Search · My Bookings · TED · Profile.
 * Profile destination adapts to user role. TED opens the floating chat via
 * a DOM click on the existing TedChat launcher (no prop-drilling needed).
 * iOS safe-area-inset-bottom is respected via `env(safe-area-inset-bottom)`.
 */
const HIDDEN_PATHS = [
  '/login',
  '/register',
];

export default function MobileBottomTabBar() {
  const location = useLocation();
  const navigate = useNavigate();
  const { language, localize } = useLanguage();
  const { user } = useAuth();
  const isAr = language === 'ar';
  const t = (ar, en) => localize({ ar, en });

  const path = location.pathname || '/';
  const hidden = HIDDEN_PATHS.some((p) => path.startsWith(p));
  if (hidden) return null;

  // ─── Role-aware destinations ────────────────────────────────────
  const role = user?.role || 'guest';
  const bookingsPath =
    role === 'teacher' ? '/teacher/bookings'
    : role === 'parent' ? '/parent/bookings'
    : role === 'admin' ? '/admin/bookings'
    : '/student/bookings'; // student & guest default

  const searchPath = role === 'teacher' ? '/teacher/students' : '/tutors';

  const profilePath =
    !user ? '/login'
    : role === 'teacher' ? '/teacher/profile'
    : role === 'parent' ? '/parent/dashboard'
    : role === 'admin' ? '/admin/dashboard'
    : '/student/profile';

  const homePath =
    !user ? '/'
    : role === 'teacher' ? '/teacher/dashboard'
    : role === 'parent' ? '/parent/dashboard'
    : role === 'admin' ? '/admin/dashboard'
    : '/student/dashboard';

  // Clicking TED opens the floating chat widget via its existing button.
  const openTed = () => {
    const btn = document.querySelector('[data-testid="ted-chat-toggle"]');
    if (btn) btn.click();
  };

  const tabs = [
    { key: 'home',     labelAr: 'الرئيسية',  labelEn: 'Home',     icon: Home,          onClick: () => navigate(homePath),   matchPath: (p) => p === homePath || p === '/' },
    { key: 'search',   labelAr: 'بحث',       labelEn: 'Search',   icon: Search,        onClick: () => navigate(searchPath), matchPath: (p) => p.startsWith(searchPath) || p.startsWith('/tutors') || p.startsWith('/browse') },
    { key: 'bookings', labelAr: 'حجوزاتي',   labelEn: 'Bookings', icon: CalendarCheck, onClick: () => navigate(bookingsPath), matchPath: (p) => p.includes('/bookings') },
    { key: 'ted',      labelAr: 'المساعد',    labelEn: 'TED',      icon: Bot,           onClick: openTed,                    matchPath: () => false, accent: true },
    { key: 'profile',  labelAr: 'حسابي',      labelEn: 'Profile',  icon: UserIcon,      onClick: () => navigate(profilePath), matchPath: (p) => p.includes('/profile') || p.includes('/account') },
  ];

  return (
    <>
      <nav
        dir={isAr ? 'rtl' : 'ltr'}
        className="md:hidden fixed bottom-0 inset-x-0 z-[70]
          bg-[rgba(15,23,42,0.92)] backdrop-blur-xl
          border-t border-white/10
          shadow-[0_-4px_24px_rgba(15,23,42,0.28)]"
        style={{ paddingBottom: 'env(safe-area-inset-bottom, 0px)' }}
        data-testid="mobile-bottom-tab-bar"
        role="navigation"
        aria-label={t('تنقل رئيسي', 'Main navigation')}
      >
        <ul className="flex items-stretch justify-around">
          {tabs.map((tab) => {
            const active = tab.matchPath(path);
            const Icon = tab.icon;
            return (
              <li key={tab.key} className="flex-1">
                <button
                  type="button"
                  onClick={tab.onClick}
                  data-testid={`tab-${tab.key}`}
                  aria-current={active ? 'page' : undefined}
                  aria-label={localize({ ar: tab.labelAr, en: tab.labelEn })}
                  className={`relative w-full flex flex-col items-center justify-center gap-0.5 py-2
                    transition-colors active:scale-[0.96] ${
                      active ? 'text-white' : 'text-slate-400 hover:text-slate-200'
                    }`}
                >
                  {/* Apple-style active indicator — a single dot that flies
                      between tabs using Framer Motion's shared layout magic. */}
                  {active && !tab.accent && (
                    <motion.span
                      layoutId="tab-active-dot"
                      className="absolute top-1 w-1.5 h-1.5 rounded-full bg-indigo-400 shadow-[0_0_8px_rgba(129,140,248,0.8)]"
                      transition={{ type: 'spring', stiffness: 480, damping: 32 }}
                      aria-hidden
                    />
                  )}
                  <span
                    className={`relative flex items-center justify-center rounded-xl transition-all ${
                      tab.accent
                        ? 'w-11 h-11 -mt-5 bg-gradient-to-br from-indigo-500 to-violet-600 text-white shadow-[0_8px_24px_rgba(99,102,241,0.45)] ring-4 ring-[rgba(15,23,42,0.92)]'
                        : active
                          ? 'w-9 h-9 bg-white/10'
                          : 'w-9 h-9'
                    }`}
                  >
                    <Icon className={tab.accent ? 'w-5 h-5' : 'w-[18px] h-[18px]'} strokeWidth={active || tab.accent ? 2.4 : 2} />
                  </span>
                  <span className={`text-[10px] leading-none font-semibold tracking-wide ${tab.accent ? 'text-indigo-300 mt-1' : ''}`}>
                    {localize({ ar: tab.labelAr, en: tab.labelEn })}
                  </span>
                </button>
              </li>
            );
          })}
        </ul>
      </nav>

      {/* Bottom spacer so content is never hidden behind the fixed bar */}
      <div className="md:hidden" style={{ height: 'calc(64px + env(safe-area-inset-bottom, 0px))' }} aria-hidden />
    </>
  );
}
